import React, {
  useCallback, memo, useState, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import { FaInfoCircle } from 'react-icons/fa';
import formatPriceRequest from '../../../helpers/formatPriceRequest';
import getCabinPrice from '../../../helpers/getCabinPrice';
import InfoBlock from '../../InfoBlock';
import InfoDropdownPopover from '../../InfoDropdownPopover';
import PriceBreakdown from '../../TravelOption/PriceBreakdown';
import { priceRequest } from '../../../store/slices/cruise';
import LimitText from '../../LimitText';
import getPriceBreakdownKey from '../../../helpers/getPriceBreakdownKey';
import CruiseRateCancellationLabel from '../../TravelOption/Rates/CruiseRateCancellationLabel';
import { checkDoShowTripConfirmationPricing } from '../../../store/slices/user';

const StateroomDetails = ({ itinerary }) => {
  const dispatch = useDispatch();

  const doShowPricing = useSelector(checkDoShowTripConfirmationPricing);
  const priceBreakdowns = useSelector((state) => state.cruise.priceBreakdowns);
  const { passengers, tripConfirmation } = useSelector((state) => state.trip);

  const handlePriceBreakdownOpen = useCallback(() => {
    dispatch(priceRequest(formatPriceRequest({
      itinerary,
      passengers,
      bookingResponses: tripConfirmation && tripConfirmation.bookingResponses,
    })));
  }, [dispatch, itinerary, passengers, tripConfirmation]);

  const [stateroomTotals, setStateroomTotals] = useState(0);

  useEffect(() => {
    const key = getPriceBreakdownKey(formatPriceRequest({
      itinerary,
      passengers,
      bookingResponses: tripConfirmation && tripConfirmation.bookingResponses,
    }));
    if (priceBreakdowns && priceBreakdowns[key]) {
      setStateroomTotals(priceBreakdowns[key]);
    }
  }, [priceBreakdowns, itinerary, passengers, tripConfirmation]);

  const getStateroomTotal = (index) => {
    let result = 0;
    if (stateroomTotals) {
      const stateroomTotal = stateroomTotals.find((i) => i.roomIndex === index);
      result = getCabinPrice(stateroomTotal);
    }
    return result;
  };

  return (
    <InfoBlock title="Cabin details">
      <div>
        {itinerary.cabins
          .filter((c) => (c))
          .map((cabin, index) => (
            <InfoBlock
              key={`cabin-${index.toString()}`}
              title={itinerary.cabins && itinerary.cabins.length > 1 ? `Stateroom ${cabin.roomIndex + 1}: ` : ''}
            >
              <div className="grid grid-cols-4 gap-1">
                <div className="row-start-1 row-end-3">
                  <img
                    src={cabin.selectedShipCabinCategory.cabinCategoryImage}
                    className="max-h-12 m-auto"
                    alt="cabin"
                  />
                </div>
                <div className="col-start-2 col-end-5">
                  <div className="sm:text-xs xl:text-sm text-gray-600">
                    {cabin.selectedShipCabinCategory.cabinCategoryName}
                  </div>
                  {cabin.selectedShipCabinCategory.cabinCategoryDescription && (
                    <LimitText text={cabin.selectedShipCabinCategory.cabinCategoryDescription.replace(/(<([^>]+)>)/gi, '')} limit={80} />
                  )}
                </div>
                <div className="col-start-2 col-end-5">{cabin.selectedRateOption.notes}</div>
              </div>
              <div className="grid grid-cols-2 text-sm mt-2">
                <div>Cabin number:</div>
                <div>{cabin.selectedShipCabin.cabinNumber}</div>
                {cabin.selectedShipCabin.selectedCabinBedType
                  && cabin.selectedShipCabin.selectedCabinBedType.bedTypeName
                  ? (
                    <>
                      <div>Bed type:</div>
                      {cabin.selectedShipCabin.selectedCabinBedType.bedTypeName}
                    </>
                  )
                  : ''}
                <div>Deck:</div>
                <div>{cabin.selectedShipCabin.cabinDeckName}</div>
                {cabin.selectedShipCabin.cabinLocations
                && cabin.selectedShipCabin.cabinLocations.length
                  ? (
                    <>
                      <div>Location:</div>
                      <div>
                        {cabin.selectedShipCabin.cabinLocations.join(', ')}
                      </div>
                    </>
                  )
                  : ''}
                {doShowPricing && (
                <>
                  <div>Cabin total:</div>
                  <InfoDropdownPopover
                    onOpen={handlePriceBreakdownOpen}
                    dropdownTextPlaceholder={(
                      <>
                        <FaInfoCircle className="mr-1 mb-1 inline-block" />
                        <NumberFormat
                          value={getStateroomTotal(cabin.roomIndex)}
                          prefix="$"
                          displayType="text"
                          thousandSeparator
                          fixedDecimalScale="true"
                          decimalScale="2"
                          className="inline-block"
                        />
                      </>
                    )}
                    alignClass="text-left"
                    popoverAlignClass="right-0"
                  >
                    <PriceBreakdown
                      payload={{
                        itinerary,
                        passengers,
                        bookingResponses: tripConfirmation && tripConfirmation.bookingResponses,
                      }}
                      stateroomNum={index}
                    />
                  </InfoDropdownPopover>
                </>
                )}
                <div className="col-span-2 mt-2">
                  <CruiseRateCancellationLabel
                    rateOptionRules={cabin.selectedRateOptionRules}
                    itineraryId={itinerary.itineraryDetail.itineraryId}
                    rateOptionCode={cabin.selectedRateOption.rateOptionCode}
                    selectedRoomIndex={cabin.roomIndex}
                    doShowRateRuleOnHover
                    cruiseLineId={itinerary.itineraryDetail.cruiseLine.cruiseLineId}
                  />
                </div>
              </div>
            </InfoBlock>
          ))}
      </div>
    </InfoBlock>
  );
};

StateroomDetails.propTypes = {
  itinerary: PropTypes.shape().isRequired,
};

export default memo(StateroomDetails);
